import React from 'react';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { useTheme } from './ThemeContext';

/**
 * App-eigener Bestätigungsdialog (Liquid-Glass) — Ersatz für window.confirm().
 *
 * Props:
 *   open          {boolean}  Dialog sichtbar
 *   message       {string}   Frage/Text (\n wird als Umbruch gerendert)
 *   confirmLabel  {string}   Beschriftung des Bestätigen-Buttons
 *   cancelLabel   {string}   Beschriftung des Abbrechen-Buttons
 *   variant       {string}   'success' (grün) | 'danger' (rot) | 'brand' (blau)
 *   processing    {boolean}  Buttons sperren während der Aktion läuft
 *   inputPlaceholder {string} Optional: zeigt ein Textfeld (z. B. Ablehngrund); Wert geht an onConfirm(value)
 *   onConfirm     {function} (inputValue?: string) => void
 *   onCancel      {function}
 */
const ConfirmDialog = ({
  open,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'brand',
  processing = false,
  inputPlaceholder,
  onConfirm,
  onCancel,
}) => {
  const { isDarkMode } = useTheme();
  const [inputValue, setInputValue] = React.useState('');

  // Eingabe bei jedem Oeffnen frisch (kein Rest vom letzten Dialog)
  React.useEffect(() => { if (open) setInputValue(''); }, [open]);

  if (!open) return null;

  const variantStyle = {
    success: { btn: 'bg-emerald-600 hover:bg-emerald-700', icon: CheckCircle, iconColor: isDarkMode ? 'text-emerald-300' : 'text-emerald-600', iconBg: isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-50' },
    danger:  { btn: 'bg-red-600 hover:bg-red-700',         icon: XCircle,     iconColor: isDarkMode ? 'text-red-300' : 'text-red-600',         iconBg: isDarkMode ? 'bg-red-500/20' : 'bg-red-50' },
    brand:   { btn: 'bg-brand hover:opacity-90',           icon: AlertTriangle, iconColor: isDarkMode ? 'text-blue-300' : 'text-brand',        iconBg: isDarkMode ? 'bg-blue-500/20' : 'bg-brand/10' },
  }[variant] || {};
  const Icon = variantStyle.icon || AlertTriangle;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={processing ? undefined : onCancel} />

      {/* Panel */}
      <div className={`relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl ${
        isDarkMode
          ? 'bg-gray-900/90 border border-white/20 backdrop-blur-xl'
          : 'bg-white border border-slate-200'
      }`}>
        <div className="px-6 py-5">
          <div className="flex items-start space-x-3">
            <div className={`p-2 rounded-xl flex-shrink-0 ${variantStyle.iconBg}`}>
              <Icon className={`w-5 h-5 ${variantStyle.iconColor}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm leading-relaxed whitespace-pre-line pt-1.5 ${isDarkMode ? 'text-white/90' : 'text-slate-700'}`}>
                {message}
              </p>
              {inputPlaceholder && (
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  rows={2}
                  placeholder={inputPlaceholder}
                  className={`mt-3 w-full px-3 py-2 text-sm rounded-2xl border focus:outline-none focus:ring-2 ${
                    isDarkMode
                      ? 'bg-gray-800/60 border-white/15 text-white placeholder-white/40 focus:ring-blue-400/40'
                      : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400 focus:ring-brand/30'
                  }`}
                />
              )}
            </div>
          </div>
        </div>

        <div className={`px-6 py-4 border-t flex justify-end space-x-3 ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
          <button
            type="button"
            onClick={onCancel}
            disabled={processing}
            className={`px-4 py-2 text-sm font-medium rounded-2xl transition-all duration-200 disabled:opacity-50 ${
              isDarkMode
                ? 'bg-gray-800/60 border border-white/20 text-white/80 hover:bg-gray-700/60'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(inputValue)}
            disabled={processing}
            className={`px-4 py-2 text-sm font-medium rounded-2xl text-white transition-all duration-200 disabled:opacity-50 ${variantStyle.btn}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
